import React from 'react';
import { withAgentCompanyQuery } from '../agentCompanyQuery';
import { requestLocalAgentUpdate } from '../localAgentUpdate';
import AgentSettingsPanel from './AgentSettingsPanel';
import './AgentRunBadges.css';

type MonitorRun = {
  run_id: string;
  skill: string;
  status: string;
  title?: string;
  draft?: {
    openTask?: string;
    kind?: string;
    proposals?: Array<{ title?: string; action?: string }>;
  };
};

type AgentDevice = {
  online?: boolean;
  last_seen_at?: string;
  last_checkin_at?: string;
  reported_at?: string;
  created_at?: string;
  hostname?: string;
  device_label?: string;
  label?: string;
  runtime?: string;
  cloud?: string;
  version?: string;
  vantage_hints?: Array<{ kind?: string; value?: string }>;
  /** Present when status fell back to another company this login can open. */
  shared?: boolean;
  shared_from_company_id?: number;
};

type AgentUpdateInfo = {
  available?: boolean;
  latest_version?: string;
  outdated_count?: number;
  unknown_version_count?: number;
  outdated_devices?: Array<{ device_id?: string; hostname?: string; version?: string | null }>;
  process_en?: string[];
  process_es?: string[];
};

type ScheduleBody = {
  checkin_cadence?: string;
  checkin?: { label_en?: string; label_es?: string; interval_hours?: number };
  discovery_cadence?: string;
  discovery?: { label_en?: string; label_es?: string };
};

const deviceSeenAt = (device: AgentDevice): string => (
  String(device.last_seen_at || device.last_checkin_at || device.reported_at || '').trim()
);

const deviceIsFresh = (device: AgentDevice, withinMs = 24 * 60 * 60 * 1000): boolean => {
  const seen = deviceSeenAt(device);
  if (!seen) return false;
  const ts = Date.parse(seen);
  if (Number.isNaN(ts)) return true;
  return Date.now() - ts <= withinMs;
};

const relativeAge = (iso: string, language: 'en' | 'es'): string => {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  const seconds = Math.max(0, Math.round((Date.now() - ts) / 1000));
  const es = language === 'es';
  if (seconds < 60) return es ? 'hace un momento' : 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return es ? `hace ${minutes} min` : `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return es ? `hace ${hours} h` : `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 60) return es ? `hace ${days} d` : `${days}d ago`;
  const months = Math.round(days / 30);
  return es ? `hace ${months} mes` : `${months}mo ago`;
};

type Props = {
  feature:
    | 'cloud-compliance'
    | 'dod-contracts-compliance'
    | 'ofac-compliance'
    | 'website-compliance'
    | 'business-creation-compliance';
  language: 'en' | 'es';
  gatewayBaseUrl: string;
  accessToken: string;
  /**
   * Console context only (project / website / account name).
   * Never presented as “where the agent runs” — that comes from device hostnames.
   */
  scopeLabel?: string;
  /** Subscription / auth company id — scopes Fleet to the account picker, not the JWT default. */
  companyId?: number | null;
  readOnly?: boolean;
  /** When a draft points at a checklist control, open that step. */
  onOpenTask?: (controlId: string) => void;
  /**
   * Open the Install agent panel when this computer cannot self-update
   * (agent not running, or an older build without POST /v1/update).
   */
  onRequestInstall?: () => void;
  /** Lets the parent relabel Install agent → Update agent when a package is behind. */
  onUpdateAvailableChange?: (available: boolean) => void;
  /** Parent (gates strip) can bump this to open Folders & Sources in-console. */
  sourcesOpenSignal?: number;
};

const FEATURE_SKILL: Record<Props['feature'], string> = {
  'cloud-compliance': 'cloud_review',
  'dod-contracts-compliance': 'dod_gap',
  'ofac-compliance': 'ofac_watch',
  'website-compliance': 'website_scan',
  'business-creation-compliance': 'fleet_discovery',
};

const SKILL_LABEL: Record<string, { en: string; es: string }> = {
  cloud_review: { en: 'Cloud review', es: 'Revisión de nube' },
  dod_gap: { en: 'Gap draft', es: 'Borrador de brechas' },
  ofac_watch: { en: 'OFAC watch', es: 'Vigilancia OFAC' },
  website_scan: { en: 'Website scan', es: 'Escaneo del sitio' },
  fleet_discovery: { en: 'Fleet discovery', es: 'Descubrimiento de flota' },
};

const SKILL_REPORTS: Record<Props['feature'], { en: string; es: string }> = {
  'cloud-compliance': {
    en: 'Cloud posture drafts for your approval',
    es: 'Borradores de postura en la nube para su aprobación',
  },
  'dod-contracts-compliance': {
    en: 'Government checklist gap drafts for your approval (never marks steps done alone)',
    es: 'Borradores de brechas del checklist del gobierno (nunca marca pasos solos)',
  },
  'ofac-compliance': {
    en: 'OFAC watch hits waiting for your decision',
    es: 'Hallazgos OFAC que esperan su decisión',
  },
  'website-compliance': {
    en: 'Website scan findings and install presence',
    es: 'Hallazgos del escaneo del sitio y presencia del agente',
  },
  'business-creation-compliance': {
    en: 'Formation proof discovery and folder matches for your approval',
    es: 'Descubrimiento de pruebas de formación y carpetas para su aprobación',
  },
};

const SCOPE_KIND: Record<Props['feature'], { en: string; es: string }> = {
  'website-compliance': { en: 'Site', es: 'Sitio' },
  'dod-contracts-compliance': { en: 'Project', es: 'Proyecto' },
  'cloud-compliance': { en: 'Account', es: 'Cuenta' },
  'ofac-compliance': { en: 'Entity', es: 'Entidad' },
  'business-creation-compliance': { en: 'Entity', es: 'Entidad' },
};

const deviceWhere = (device: AgentDevice): string => (
  String(device.hostname || device.device_label || device.label || '').trim()
);

/** Human label for where the agent binary said it is running — never "cloud ?". */
export const envPhrase = (device: AgentDevice, language: 'en' | 'es'): string => {
  const es = language === 'es';
  const cloud = String(device.cloud || '').trim().toLowerCase();
  const runtime = String(device.runtime || '').trim().toLowerCase();
  const cloudKnown = Boolean(cloud && cloud !== 'unknown');
  const cloudPart = cloud === 'aws'
    ? 'AWS'
    : cloud === 'gcp'
      ? 'Google Cloud'
      : cloud === 'azure'
        ? 'Microsoft Azure'
        : cloudKnown
          ? cloud
          : '';
  const runtimePart = runtime === 'cluster'
    ? 'Kubernetes'
    : runtime === 'container'
      ? 'Docker'
      : runtime === 'compute'
        ? (es ? 'equipo / VM' : 'this computer / VM')
        : runtime && runtime !== 'unknown'
          ? runtime
          : '';
  if (cloudKnown && runtimePart) return `${cloudPart} · ${runtimePart}`;
  if (cloudKnown) return cloudPart;
  if (runtime === 'compute' || !runtime || runtime === 'unknown') {
    return es
      ? 'Este equipo / on-prem (sin nube detectada)'
      : 'This computer / on-prem (no cloud detected)';
  }
  return es
    ? `${runtimePart} · nube no detectada`
    : `${runtimePart} · cloud not detected`;
};

/**
 * Live agent strip: presence, environment the binary reported, check-in age,
 * schedule cadence, and what work is waiting — plus a settings cog.
 */
export default function AgentRunBadges({
  feature,
  language,
  gatewayBaseUrl,
  accessToken,
  scopeLabel,
  companyId = null,
  readOnly = false,
  onOpenTask,
  onRequestInstall,
  onUpdateAvailableChange,
  sourcesOpenSignal = 0,
}: Props): React.ReactElement | null {
  const gateway = gatewayBaseUrl.replace(/\/$/, '');
  const skill = FEATURE_SKILL[feature];
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const updatePanelRef = React.useRef<HTMLDivElement>(null);
  const [online, setOnline] = React.useState(0);
  const [devices, setDevices] = React.useState<AgentDevice[]>([]);
  const [runs, setRuns] = React.useState<MonitorRun[]>([]);
  const [schedule, setSchedule] = React.useState<ScheduleBody | null>(null);
  const [updateInfo, setUpdateInfo] = React.useState<AgentUpdateInfo | null>(null);
  const [updateOpen, setUpdateOpen] = React.useState(false);
  const [updateBusy, setUpdateBusy] = React.useState(false);
  const [updateNote, setUpdateNote] = React.useState('');
  const [updateError, setUpdateError] = React.useState('');
  const [loaded, setLoaded] = React.useState(false);
  const [refreshBusy, setRefreshBusy] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [settingsFocus, setSettingsFocus] = React.useState<'drafts' | 'schedule' | 'sources'>('schedule');
  const [folderCount, setFolderCount] = React.useState(0);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const cogRef = React.useRef<HTMLButtonElement>(null);
  const sourcesRef = React.useRef<HTMLButtonElement>(null);
  const reloadRef = React.useRef<(() => void) | null>(null);
  const lastSourcesSignal = React.useRef(0);

  const openSources = React.useCallback(() => {
    setSettingsFocus('sources');
    setSettingsOpen(true);
  }, []);

  React.useEffect(() => {
    if (!sourcesOpenSignal || sourcesOpenSignal === lastSourcesSignal.current) return;
    lastSourcesSignal.current = sourcesOpenSignal;
    openSources();
  }, [openSources, sourcesOpenSignal]);

  const runLocalUpdate = React.useCallback(async () => {
    if (updateBusy || readOnly || !accessToken) return;
    const spanish = language === 'es';
    setUpdateBusy(true);
    setUpdateError('');
    setUpdateNote(spanish
      ? 'Actualizando el agente en este equipo…'
      : 'Updating the agent on this computer…');
    setUpdateOpen(true);
    setDetailsOpen(true);
    const result = await requestLocalAgentUpdate({ gateway });
    if (result.ok) {
      setUpdateNote(spanish
        ? `Listo${result.version ? ` (v${result.version})` : ''}. Esperando check-in…`
        : `Done${result.version ? ` (v${result.version})` : ''}. Waiting for check-in…`);
      window.setTimeout(() => {
        reloadRef.current?.();
      }, 2500);
      window.setTimeout(() => {
        reloadRef.current?.();
      }, 8000);
      setUpdateBusy(false);
      return;
    }

    // Retry with a fresh install token when the running agent needs auth for the package.
    if (!result.missing) {
      try {
        const tokenRes = await fetch(`${gateway}${withAgentCompanyQuery('/api/v1/agent/install-token', companyId)}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            label: String(scopeLabel || 'site-1').trim() || 'site-1',
            ...(companyId ? { company_id: companyId } : {}),
          }),
        });
        const tokenBody = await tokenRes.json() as { token?: string; detail?: string };
        const token = String(tokenBody.token || '').trim();
        if (tokenRes.ok && token) {
          const retry = await requestLocalAgentUpdate({
            gateway,
            token,
            label: String(scopeLabel || '').trim() || undefined,
          });
          if (retry.ok) {
            setUpdateNote(spanish
              ? `Listo${retry.version ? ` (v${retry.version})` : ''}. Esperando check-in…`
              : `Done${retry.version ? ` (v${retry.version})` : ''}. Waiting for check-in…`);
            window.setTimeout(() => reloadRef.current?.(), 2500);
            window.setTimeout(() => reloadRef.current?.(), 8000);
            setUpdateBusy(false);
            return;
          }
        }
      } catch {
        // Fall through to install guidance.
      }
    }

    // Never browser-download an unsigned binary (Gatekeeper blocks it). Open Install instead.
    setUpdateError('');
    setUpdateNote(spanish
      ? 'No hay un agente en ejecución en este Mac (o es demasiado antiguo para auto-actualizar). Abra Instalar agente → Este equipo → Generar token y copiar, péguelo en Terminal, y deje el agente en segundo plano.'
      : 'No agent is running on this Mac (or it is too old to self-update). Open Install agent → This computer → Generate token & copy, paste in Terminal, and leave the agent running in the background.');
    onRequestInstall?.();
    setUpdateBusy(false);
  }, [accessToken, companyId, gateway, language, onRequestInstall, readOnly, scopeLabel, updateBusy]);

  React.useEffect(() => {
    onUpdateAvailableChange?.(Boolean(updateInfo?.available));
  }, [onUpdateAvailableChange, updateInfo?.available]);

  const openDraft = (_run: MonitorRun) => {
    // Always open the settings panel on drafts — Approve / Reject / Open step live there.
    // Do not jump straight to a checklist row; that felt like “nothing happened.”
    setSettingsFocus('drafts');
    setSettingsOpen(true);
  };

  React.useEffect(() => {
    if (!accessToken || !gateway) {
      setLoaded(true);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const [monitorRes, statusRes, scheduleRes] = await Promise.all([
          fetch(`${gateway}${withAgentCompanyQuery('/api/v1/agent/monitor', companyId)}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
          fetch(`${gateway}${withAgentCompanyQuery('/api/v1/agent/status', companyId)}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
          fetch(`${gateway}${withAgentCompanyQuery('/api/v1/agent/schedule', companyId)}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
        ]);
        if (cancelled) return;
        if (monitorRes.ok) {
          const body = await monitorRes.json() as {
            awaiting?: MonitorRun[];
            recent?: MonitorRun[];
          };
          const open = [
            ...(Array.isArray(body.awaiting) ? body.awaiting : []),
            ...(Array.isArray(body.recent) ? body.recent : []).filter((run) => (
              run.status === 'running' || run.status === 'awaiting_human'
            )),
          ].filter((run) => run.skill === skill || FEATURE_SKILL[feature] === run.skill);
          const byId = new Map(open.map((run) => [run.run_id, run]));
          setRuns(Array.from(byId.values()));
        }
        if (statusRes.ok) {
          const body = await statusRes.json() as {
            devices?: AgentDevice[];
            latest_version?: string;
            update?: AgentUpdateInfo;
          };
          const list = Array.isArray(body.devices) ? body.devices : [];
          setDevices(list);
          setOnline(list.filter((device) => (
            device.online !== false && deviceSeenAt(device) && deviceIsFresh(device)
          )).length);
          setUpdateInfo(
            body.update && typeof body.update === 'object'
              ? body.update
              : null,
          );
        }
        if (scheduleRes.ok) {
          const body = await scheduleRes.json() as ScheduleBody & { scan_roots?: Array<unknown> };
          setSchedule(body);
          setFolderCount(Array.isArray(body.scan_roots) ? body.scan_roots.length : 0);
        }
      } catch {
        // Badges are chrome; silence network noise.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    reloadRef.current = () => {
      void load();
    };
    void load();
    // Keep Online / Installed fresh without a full page refresh after Terminal install.
    const poll = window.setInterval(() => {
      void load();
    }, 20_000);
    return () => {
      cancelled = true;
      window.clearInterval(poll);
    };
  }, [accessToken, companyId, feature, gateway, skill, settingsOpen]);

  if (!accessToken || !gateway || !loaded) return null;

  const es = language === 'es';
  const tone = online > 0 ? 'online' : devices.length > 0 ? 'idle' : 'offline';
  const lastSeenIso = (() => {
    const primaryDevice = devices[0];
    return primaryDevice ? deviceSeenAt(primaryDevice) : '';
  })();
  const lastSeenShort = lastSeenIso ? relativeAge(lastSeenIso, language) : '';
  const presence = tone === 'online'
    ? (es
      ? `En línea${lastSeenShort ? ` · ${lastSeenShort}` : ''}`
      : `Online${lastSeenShort ? ` · ${lastSeenShort}` : ''}`)
    : tone === 'idle'
      ? (es
        ? `Instalado${lastSeenShort ? ` · ${lastSeenShort}` : ''}`
        : `Installed${lastSeenShort ? ` · ${lastSeenShort}` : ''}`)
      : (es ? 'Sin agente' : 'No agent');

  const primary = devices[0];
  const sharedFleet = Boolean(primary?.shared);
  const hostNames = Array.from(new Set(devices.map(deviceWhere).filter(Boolean)));
  const whereLabel = hostNames.length === 0
    ? ''
    : hostNames.length === 1
      ? (es ? `en ${hostNames[0]}` : `on ${hostNames[0]}`)
      : (es
        ? `en ${hostNames.slice(0, 2).join(', ')}${hostNames.length > 2 ? ` +${hostNames.length - 2}` : ''}`
        : `on ${hostNames.slice(0, 2).join(', ')}${hostNames.length > 2 ? ` +${hostNames.length - 2}` : ''}`);

  const envLabel = primary
    ? `${envPhrase(primary, language)}${sharedFleet ? (es ? ' · flota compartida' : ' · shared fleet') : ''}`
    : (es
      ? 'Sin entorno aún — el agente AWS/CLI se une a la empresa del token de instalación'
      : 'No environment yet — AWS/CLI agent binds to the company on the install token');

  const enrolledIso = String(primary?.created_at || '').trim();
  const lastSeenLabel = lastSeenIso
    ? (es ? `Último check-in ${relativeAge(lastSeenIso, language)}` : `Last check-in ${relativeAge(lastSeenIso, language)}`)
    : (es ? 'Sin check-in aún' : 'No check-in yet');
  const enrolledLabel = enrolledIso
    ? (es ? `Instalado ${relativeAge(enrolledIso, language)}` : `Installed ${relativeAge(enrolledIso, language)}`)
    : '';

  const cadenceLabel = (() => {
    if (!schedule) return es ? 'Frecuencia: —' : 'Frequency: —';
    const hours = Number(schedule.checkin?.interval_hours);
    const preset = language === 'es'
      ? (schedule.checkin?.label_es || schedule.checkin_cadence || '')
      : (schedule.checkin?.label_en || schedule.checkin_cadence || '');
    if (schedule.checkin_cadence === 'manual' || hours === 0) {
      return es ? 'Check-in: solo manual' : 'Check-in: manual only';
    }
    if (Number.isFinite(hours) && hours > 0) {
      return es ? `Check-in cada ${hours} h` : `Check-in every ${hours}h`;
    }
    return es ? `Check-in: ${preset}` : `Check-in: ${preset}`;
  })();

  const hintCount = devices.reduce((sum, device) => (
    sum + (Array.isArray(device.vantage_hints) ? device.vantage_hints.length : 0)
  ), 0);
  const reportsBase = es ? SKILL_REPORTS[feature].es : SKILL_REPORTS[feature].en;
  const reportsHeartbeat = es
    ? 'En cada check-in envía: cloud, runtime, hostname (solo lectura).'
    : 'Each check-in sends: cloud, runtime, hostname (read-only).';
  const reportsHints = hintCount > 0
    ? (es ? ` · ${hintCount} dominio(s) de pista` : ` · ${hintCount} domain hint(s)`)
    : '';

  const scopeKind = SCOPE_KIND[feature];
  const contextLabel = String(scopeLabel || '').trim()
    ? `${es ? scopeKind.es : scopeKind.en}: ${String(scopeLabel).trim()}`
    : '';

  const skillName = (key: string) => {
    const row = SKILL_LABEL[key];
    return row ? (es ? row.es : row.en) : key;
  };
  const statusLabel = (status: string) => {
    if (status === 'running') return es ? 'ejecutando' : 'running';
    if (status === 'awaiting_human') return es ? 'necesita su decisión' : 'needs you';
    return status;
  };
  const settingsLabel = es ? 'Ajustes del agente' : 'Agent settings';
  const sourcesLabel = es ? 'Fuentes' : 'Sources';
  const sourcesTitle = es
    ? 'Carpetas y fuentes de prueba (correo, Drive, Dropbox…). Un agente por equipo; el RBAC decide los proyectos.'
    : 'Folders and proof sources (email, Drive, Dropbox…). One agent per computer; RBAC decides project access.';
  const sourcesNeedSetup = folderCount <= 0;
  const detailsLabel = es ? 'Detalle del agente' : 'Agent details';
  const toggleDetailsLabel = detailsOpen
    ? (es ? 'Ocultar detalle del agente' : 'Hide agent details')
    : (es ? 'Mostrar detalle del agente' : 'Show agent details');

  const toggleDetails = () => setDetailsOpen((current) => !current);

  return (
    <div className="agent-run-badges-wrap" ref={wrapRef}>
      <div className="agent-run-badges__chrome">
        <button
          type="button"
          ref={sourcesRef}
          className={`agent-sources-btn${settingsOpen && settingsFocus === 'sources' ? ' is-open' : ''}${sourcesNeedSetup ? ' is-needs-setup' : ''}`}
          aria-label={sourcesLabel}
          aria-expanded={settingsOpen && settingsFocus === 'sources'}
          aria-haspopup="dialog"
          title={sourcesTitle}
          onClick={(event) => {
            event.stopPropagation();
            if (settingsOpen && settingsFocus === 'sources') {
              setSettingsOpen(false);
              return;
            }
            openSources();
          }}
        >
          <span className="agent-sources-btn__icon" aria-hidden="true">
            <svg viewBox="0 0 32 32" width="22" height="22" focusable="false">
              <path fill="currentColor" d="M4 8.5A2.5 2.5 0 0 1 6.5 6H13l2 2h10.5A2.5 2.5 0 0 1 28 10.5v13A2.5 2.5 0 0 1 25.5 26h-19A2.5 2.5 0 0 1 4 23.5v-15z" opacity="0.35" />
              <path fill="currentColor" d="M6 12.5A2.5 2.5 0 0 1 8.5 10H15l1.8 1.8H25.5A2.5 2.5 0 0 1 28 14.3V24a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2v-11.5z" />
              <circle cx="23.2" cy="21.2" r="5.2" fill="var(--color-surface, #fff)" />
              <path fill="currentColor" d="M23.2 17.6a3.6 3.6 0 1 0 0 7.2 3.6 3.6 0 0 0 0-7.2zm0 1.3a.7.7 0 0 1 .7.7v.2h.9a.7.7 0 0 1 0 1.4h-.9v.9a.7.7 0 0 1-1.4 0v-.9h-.9a.7.7 0 0 1 0-1.4h.9v-.2a.7.7 0 0 1 .7-.7z" />
            </svg>
          </span>
          <span className="agent-sources-btn__copy">
            <strong>{sourcesLabel}</strong>
            <em>
              {sourcesNeedSetup
                ? (es ? 'Elegir carpetas' : 'Set folders')
                : (es ? `${folderCount} carpeta(s)` : `${folderCount} folder(s)`)}
            </em>
          </span>
        </button>
      </div>
      <div
        className={`agent-run-badges is-${tone}${detailsOpen ? ' is-open' : ''}`}
        role="button"
        tabIndex={0}
        aria-expanded={detailsOpen}
        aria-controls="agent-run-badges-details"
        aria-label={`${es ? 'Agentes en este proyecto' : 'Agents on this project'}. ${toggleDetailsLabel}`}
        title={toggleDetailsLabel}
        onClick={toggleDetails}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleDetails();
          }
        }}
      >
        <span className="agent-run-badges__presence">
          <i className="agent-run-badges__dot" aria-hidden="true" />
          {presence}
        </span>
        {whereLabel ? (
          <span className="agent-run-badges__where" title={hostNames.join(', ')}>
            {whereLabel}
          </span>
        ) : null}
        <span
          className="agent-run-badges__env"
          title={es ? 'Entorno reportado por el agente' : 'Environment reported by the agent'}
        >
          {envLabel}
        </span>
        {contextLabel ? (
          <span className="agent-run-badges__scope" title={es ? 'Contexto de la consola' : 'Console context'}>
            {contextLabel}
          </span>
        ) : null}
        {runs.length ? (
          <ul className="agent-run-badges__list">
            {runs.map((run) => (
              <li key={run.run_id}>
                <button
                  type="button"
                  className={`agent-run-badges__chip is-${run.status === 'running' ? 'running' : 'waiting'}`}
                  title={
                    run.status === 'awaiting_human'
                      ? (es
                        ? 'Abrir el borrador para aprobar, rechazar o ir al paso'
                        : 'Open the draft to approve, reject, or go to the step')
                      : (run.title || skillName(run.skill))
                  }
                  aria-label={
                    `${skillName(run.skill)} · ${statusLabel(run.status)}. ${
                      es ? 'Pulse para revisar.' : 'Click to review.'
                    }`
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                    openDraft(run);
                  }}
                >
                  <span className="agent-run-badges__icon" aria-hidden="true">◎</span>
                  {skillName(run.skill)}
                  {' · '}
                  {statusLabel(run.status)}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {updateInfo?.available ? (
          <button
            type="button"
            className={`agent-run-badges__chip is-update${updateOpen ? ' is-open' : ''}${updateBusy ? ' is-busy' : ''}`}
            aria-expanded={updateOpen || detailsOpen}
            aria-controls="agent-run-badges-update"
            disabled={updateBusy || readOnly}
            title={es
              ? 'Actualiza el agente en este equipo (descarga + reinicio).'
              : 'Updates the agent on this computer (download + restart).'}
            onClick={(event) => {
              event.stopPropagation();
              void runLocalUpdate();
            }}
          >
            <span className="agent-run-badges__icon" aria-hidden="true">↑</span>
            {updateBusy
              ? (es ? 'Actualizando…' : 'Updating…')
              : (es
                ? `Actualizar · v${updateInfo.latest_version || '?'}`
                : `Update · v${updateInfo.latest_version || '?'}`)}
          </button>
        ) : null}
        <button
          type="button"
          className={`agent-run-badges__chip is-refresh${refreshBusy ? ' is-busy' : ''}`}
          aria-label={es ? 'Actualizar estado del agente' : 'Refresh agent status'}
          title={es
            ? 'Actualizar ahora (también se refresca solo cada 20 s)'
            : 'Refresh now (also auto-refreshes every 20s)'}
          disabled={refreshBusy}
          onClick={(event) => {
            event.stopPropagation();
            setRefreshBusy(true);
            reloadRef.current?.();
            window.setTimeout(() => setRefreshBusy(false), 800);
          }}
        >
          <span className={`agent-run-badges__icon${refreshBusy ? ' is-spin' : ''}`} aria-hidden="true">↻</span>
          {es ? 'Actualizar' : 'Refresh'}
        </button>
        <span className="agent-run-badges__chevron" aria-hidden="true" />
        <button
          type="button"
          ref={cogRef}
          className={`agent-run-badges__cog${settingsOpen ? ' is-open' : ''}`}
          aria-label={settingsLabel}
          aria-expanded={settingsOpen}
          aria-haspopup="dialog"
          title={settingsLabel}
          onClick={(event) => {
            event.stopPropagation();
            setSettingsFocus('schedule');
            setSettingsOpen((current) => !current);
          }}
        >
          <span aria-hidden="true">⚙</span>
        </button>
      </div>

      {detailsOpen ? (
        <div
          id="agent-run-badges-details"
          className="agent-run-badges__details"
          role="region"
          aria-label={detailsLabel}
        >
          <div className="agent-run-badges__detail">
            <span className="agent-run-badges__detail-k">{es ? 'Entorno' : 'Environment'}</span>
            <span className="agent-run-badges__detail-v">
              {primary
                ? `${envLabel}${whereLabel ? ` · ${whereLabel}` : ''}`
                : (es
                  ? 'Instale el agente; reportará cloud + runtime + hostname en el check-in.'
                  : 'Install the agent; it will report cloud + runtime + hostname on check-in.')}
            </span>
          </div>
          <div className="agent-run-badges__detail">
            <span className="agent-run-badges__detail-k">{es ? 'Actividad' : 'Uptime'}</span>
            <span className="agent-run-badges__detail-v">
              {[enrolledLabel, lastSeenLabel].filter(Boolean).join(' · ') || (es ? '—' : '—')}
            </span>
          </div>
          <div className="agent-run-badges__detail">
            <span className="agent-run-badges__detail-k">{es ? 'Frecuencia' : 'Frequency'}</span>
            <span className="agent-run-badges__detail-v">{cadenceLabel}</span>
          </div>
          <div className="agent-run-badges__detail agent-run-badges__detail--wide">
            <span className="agent-run-badges__detail-k">{es ? 'Qué reporta' : 'Reporting'}</span>
            <span className="agent-run-badges__detail-v">
              {reportsHeartbeat}
              {reportsHints}
              {' · '}
              {reportsBase}
            </span>
          </div>
          {updateInfo?.available || updateNote || updateError ? (
            <div
              id="agent-run-badges-update"
              ref={updatePanelRef}
              className="agent-run-badges__detail agent-run-badges__detail--wide agent-run-badges__update"
              role="region"
              aria-label={es ? 'Actualización del agente' : 'Agent update'}
            >
              <span className="agent-run-badges__detail-k">{es ? 'Actualización' : 'Update'}</span>
              <div className="agent-run-badges__detail-v">
                <p>
                  {updateNote || (es
                    ? `${updateInfo?.outdated_count || 0} equipo(s) necesitan el paquete del gateway (v${updateInfo?.latest_version || '?'}). Pulse Actualizar para instalarlo en este equipo.`
                    : `${updateInfo?.outdated_count || 0} device(s) need the gateway package (v${updateInfo?.latest_version || '?'}). Click Update to install it on this computer.`)}
                </p>
                {updateError ? <p className="agent-run-badges__update-error" role="alert">{updateError}</p> : null}
                <div className="agent-run-badges__update-actions">
                  <button
                    type="button"
                    className="dashboard-btn dashboard-btn-primary"
                    disabled={updateBusy || readOnly}
                    onClick={(event) => {
                      event.stopPropagation();
                      void runLocalUpdate();
                    }}
                  >
                    {updateBusy
                      ? (es ? 'Actualizando…' : 'Updating…')
                      : (es ? 'Actualizar ahora' : 'Update now')}
                  </button>
                  {onRequestInstall ? (
                    <button
                      type="button"
                      className="dashboard-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        onRequestInstall();
                      }}
                    >
                      {es ? 'Instalar agente (manual)' : 'Install agent (manual)'}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          ) : primary?.version ? (
            <div className="agent-run-badges__detail">
              <span className="agent-run-badges__detail-k">{es ? 'Versión' : 'Version'}</span>
              <span className="agent-run-badges__detail-v">
                {`v${primary.version}`}
                {updateInfo?.latest_version ? ` · gateway v${updateInfo.latest_version}` : ''}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      <AgentSettingsPanel
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        feature={feature}
        language={language}
        gatewayBaseUrl={gateway}
        accessToken={accessToken}
        companyId={companyId}
        readOnly={readOnly}
        anchorRef={settingsFocus === 'sources' ? sourcesRef : cogRef}
        focusSection={settingsFocus}
        onOpenTask={onOpenTask}
      />
    </div>
  );
}
